import {
  ChevronDownIcon,
  ChevronRightIcon,
  Trash,
  AlertCircle as AlertCircleIcon,
  CheckIcon,
} from 'lucide-react-native';
import { Controller, UseFormReturn } from 'react-hook-form';

import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionTrigger,
  AccordionTitleText,
  AccordionIcon,
  AccordionContent,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Checkbox, CheckboxIndicator, CheckboxIcon } from '@/components/ui/checkbox';
import {
  FormControl,
  FormControlLabel,
  FormControlLabelText,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Input, InputField } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';

interface Education {
  degree: string;
  institution: string;
  studyField: string;
  startYear?: string;
  endYear?: string;
  isPresent?: boolean;
}

interface EducationEntryProps {
  field: any;
  index: number;
  form: UseFormReturn<{ education: Education[] }>;
  hasSubmitted: boolean;
  onDeleteRequest: (index: number) => void;
  expandedItem: string[];
  setExpandedItem: (value: string[]) => void;
}

export function EducationEntry({
  index,
  form,
  hasSubmitted,
  onDeleteRequest,
  expandedItem,
  setExpandedItem,
}: EducationEntryProps) {
  const formatDateRange = (startYear?: string, endYear?: string, isPresent?: boolean) => {
    if (!startYear && !endYear && !isPresent) {
      return '';
    }

    return `${startYear || ''}${startYear && (endYear || isPresent) ? '–' : ''}${isPresent ? 'Present' : endYear || ''}`;
  };

  return (
    <Accordion
      type="single"
      collapsable
      className="mb-1 w-full shadow-none web:mb-3"
      value={expandedItem}
      onValueChange={setExpandedItem}>
      <AccordionItem value={`item-${index}`}>
        <AccordionHeader className="m-0 w-full p-0">
          <AccordionTrigger className="m-0 w-full p-0">
            {({ isExpanded }: { isExpanded: boolean }) => (
              <HStack className="m-0 w-full items-start justify-between p-0">
                <HStack className="m-0 w-0 min-w-0 flex-1 items-start gap-2 p-0">
                  {isExpanded ? (
                    <AccordionIcon as={ChevronDownIcon} className="mt-1.5" />
                  ) : (
                    <AccordionIcon as={ChevronRightIcon} className="mt-1.5" />
                  )}
                  <VStack className="min-w-0 flex-1">
                    <AccordionTitleText className="max-w-full truncate text-base">
                      <Text className="font-medium text-base text-primary-700">
                        {form.watch(`education.${index}.degree`) ||
                          (form.watch(`education.${index}.institution`)
                            ? form.watch(`education.${index}.institution`)
                            : 'New Education Entry')}
                        {form.watch(`education.${index}.institution`) &&
                          form.watch(`education.${index}.degree`) &&
                          ` – ${form.watch(`education.${index}.institution`)}`}
                      </Text>
                    </AccordionTitleText>
                    <Text className="text-sm text-primary-700">
                      {formatDateRange(
                        form.watch(`education.${index}.startYear`),
                        form.watch(`education.${index}.endYear`),
                        form.watch(`education.${index}.isPresent`)
                      )}
                    </Text>
                    {!!form.formState.errors?.education?.[index] && !isExpanded && (
                      <HStack className="mt-1 items-center">
                        <Icon as={AlertCircleIcon} size="xs" className="mr-1 text-error-600" />
                        <Text className="text-xs text-error-600">
                          Complete required fields for this entry
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                </HStack>
                <Button
                  variant="link"
                  size="md"
                  onPress={() => onDeleteRequest(index)}
                  className="ml-1 flex-shrink-0 self-start p-2">
                  <Icon as={Trash} size="md" className="m-0 p-0 text-primary-600" />
                </Button>
              </HStack>
            )}
          </AccordionTrigger>
        </AccordionHeader>
        <AccordionContent>
          <VStack space="lg" className="pt-4">
            <HStack className="w-full flex-col gap-2 md:flex-row">
              <Controller
                control={form.control}
                name={`education.${index}.degree`}
                rules={{
                  required: {
                    value: true,
                    message: 'Degree is required',
                  },
                  validate: (value) => !!value.trim() || 'Degree is required',
                }}
                render={({ field, fieldState }) => (
                  <FormControl isInvalid={!!fieldState.error} className="flex-1">
                    <FormControlLabel>
                      <FormControlLabelText>Degree</FormControlLabelText>
                    </FormControlLabel>
                    <Input size="sm">
                      <InputField
                        placeholder="e.g., MD, PhD, MPH, Fellowship"
                        value={field.value}
                        onChangeText={field.onChange}
                        onBlur={field.onBlur}
                      />
                    </Input>
                    <FormControlError>
                      <FormControlErrorIcon as={AlertCircleIcon} size="xs" />
                      <FormControlErrorText size="xs">
                        {fieldState.error?.message || 'Degree is required'}
                      </FormControlErrorText>
                    </FormControlError>
                  </FormControl>
                )}
              />

              <Controller
                control={form.control}
                name={`education.${index}.studyField`}
                rules={{
                  required: {
                    value: true,
                    message: 'Field is required',
                  },
                  validate: (value) => !!value.trim() || 'Field is required',
                }}
                render={({ field, fieldState }) => (
                  <FormControl isInvalid={!!fieldState.error} isRequired className="flex-1">
                    <FormControlLabel>
                      <FormControlLabelText>Field</FormControlLabelText>
                    </FormControlLabel>
                    <Input size="sm">
                      <InputField
                        placeholder="e.g., Oncology"
                        value={field.value}
                        onChangeText={field.onChange}
                        onBlur={field.onBlur}
                      />
                    </Input>
                    <FormControlError>
                      <FormControlErrorIcon as={AlertCircleIcon} size="xs" />
                      <FormControlErrorText size="xs">
                        {fieldState.error?.message || 'Field is required'}
                      </FormControlErrorText>
                    </FormControlError>
                  </FormControl>
                )}
              />
            </HStack>

            <Controller
              control={form.control}
              name={`education.${index}.institution`}
              rules={{
                required: {
                  value: true,
                  message: 'Institution is required',
                },
                validate: (value) => !!value.trim() || 'Institution is required',
              }}
              render={({ field, fieldState }) => (
                <FormControl isInvalid={!!fieldState.error} isRequired className="w-full">
                  <FormControlLabel>
                    <FormControlLabelText>Institution</FormControlLabelText>
                  </FormControlLabel>
                  <Input size="sm">
                    <InputField
                      placeholder="e.g., Harvard University"
                      value={field.value}
                      onChangeText={field.onChange}
                      onBlur={field.onBlur}
                    />
                  </Input>
                  <FormControlError>
                    <FormControlErrorIcon as={AlertCircleIcon} size="xs" />
                    <FormControlErrorText size="xs">
                      {fieldState.error?.message || 'Institution is required'}
                    </FormControlErrorText>
                  </FormControlError>
                </FormControl>
              )}
            />

            <HStack className="w-full items-start gap-2">
              <Controller
                control={form.control}
                name={`education.${index}.startYear`}
                render={({ field, fieldState }) => (
                  <FormControl isInvalid={!!fieldState.error} className="flex-1">
                    <FormControlLabel>
                      <FormControlLabelText>Start Year</FormControlLabelText>
                    </FormControlLabel>
                    <Input size="sm">
                      <InputField
                        placeholder="YYYY"
                        value={field.value}
                        onChangeText={(value) => {
                          field.onChange(value);

                          setTimeout(() => {
                            form.trigger(`education.${index}.startYear`);

                            form.trigger(`education.${index}.endYear`);
                          }, 100);
                        }}
                        onBlur={() => {
                          field.onBlur();

                          form.trigger(`education.${index}.startYear`);
                        }}
                      />
                    </Input>
                    <FormControlError>
                      <FormControlErrorIcon as={AlertCircleIcon} size="xs" />
                      <FormControlErrorText size="xs">
                        {fieldState.error?.message}
                      </FormControlErrorText>
                    </FormControlError>
                  </FormControl>
                )}
              />

              <VStack className="flex-1">
                <Controller
                  control={form.control}
                  name={`education.${index}.endYear`}
                  render={({ field, fieldState }) => (
                    <FormControl
                      isInvalid={!!fieldState.error}
                      className="flex-1"
                      isDisabled={form.watch(`education.${index}.isPresent`)}>
                      <FormControlLabel>
                        <FormControlLabelText>End Year</FormControlLabelText>
                      </FormControlLabel>
                      <Input size="sm" isDisabled={form.watch(`education.${index}.isPresent`)}>
                        <InputField
                          placeholder="YYYY"
                          value={field.value}
                          onChangeText={(value) => {
                            field.onChange(value);

                            setTimeout(() => {
                              form.trigger(`education.${index}.startYear`);

                              form.trigger(`education.${index}.endYear`);
                            }, 100);
                          }}
                          onBlur={() => {
                            field.onBlur();

                            form.trigger(`education.${index}.endYear`);
                          }}
                          editable={!form.watch(`education.${index}.isPresent`)}
                        />
                      </Input>
                      <FormControlError>
                        <FormControlErrorIcon as={AlertCircleIcon} size="xs" />
                        <FormControlErrorText size="xs">
                          {fieldState.error?.message}
                        </FormControlErrorText>
                      </FormControlError>
                    </FormControl>
                  )}
                />
                <FormControl className="mt-1 flex flex-row items-center">
                  <Controller
                    control={form.control}
                    name={`education.${index}.isPresent`}
                    render={({ field }) => (
                      <HStack className="items-center">
                        <Checkbox
                          value="present"
                          isChecked={field.value}
                          onChange={(checked) => {
                            field.onChange(checked);

                            if (checked) {
                              form.setValue(`education.${index}.endYear`, '');
                            }
                          }}
                          size="md"
                          className="mr-2">
                          <CheckboxIndicator>
                            <CheckboxIcon as={CheckIcon} />
                          </CheckboxIndicator>
                        </Checkbox>
                        <FormControlLabelText>Present</FormControlLabelText>
                      </HStack>
                    )}
                  />
                </FormControl>
              </VStack>
            </HStack>
          </VStack>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
